<template>
  <base-dialog content-class="overflow-x-hidden" fullscreen hide-overlay v-model="dialogVisible" persistent>
    <component
      :is="viewer"
      :editable="editable"
      :event="current"
      @pollEvent="pollEvent"
      :hasPrevious="index > 0"
      :hasNext="index < events.length - 1"
      @previous="indexChanged(index - 1)"
      @next="indexChanged(index + 1)"
      @deleteEvent="() => { deleteEventConfirmDialogVisible = true }"
      @updateEvent="updateEvent"
      @updateThreshold="updateThreshold"
      :sensorType="sensor.type"
      :title="sensor.name"
      @close="close"
      v-if="visible"
    >
      <template #thumbnails>
        <thumbnail-slider
          :items="events"
          keyName="id"
          :value="index"
          @scrolledToEnd="tryLoadNextEvents"
        >
          <template #item="props">
            <event-thumbnail
              :event="props.item"
              :class="{ active: index == props.index, 'selectable-thumbnail': true }"
              @click="index = props.index"
            />
          </template>
        </thumbnail-slider>
      </template>
    </component>
    <confirm-dialog
      v-model="deleteEventConfirmDialogVisible"
      :message="$t('msg.delete_event_confirm')"
      @yes="deleteEvent"
    />
  </base-dialog>
</template>

<i18n lang="yaml">
ja:
  msg:
    delete_event_confirm: このイベントを削除します、よろしいですか？
    deleted: イベントを削除しました。
    delete_event_error: イベントの削除中にエラーが発生しました
    update_threshold_success: しきい値を更新しました
    update_threshold_error: しきい値の更新中にエラーが発生しました
    update_event_error: イベントの更新中にエラーが発生しました
    not_found: |
      指定されたセンサーが存在しません。
      識別子が間違っている、または共有が解除された可能性があります。

en:
  msg:
    delete_event_confirm: This event will be deleted. Are you sure?
    deleted: Event was deleted.
    delete_event_error: 'An error occurred while deleting the event.'
    update_threshold_success: 'Threshold was successfully updated.'
    update_threshold_error: 'An error occurred while updating the threshold.'
    update_event_error: 'An error occurred while updating the event.'
    not_found: |
      The specified sensor does not exist.
      Either the wrong id was specified or your permissions on this sensor have been revoked.
</i18n>

<script>
import Vue from 'vue';
import { createPromiseQueue } from '@/libs/promiseQueue';
import SensorTypes from '@/mixins/sensorTypes';
import EventContainer from '@/mixins/eventContainer';
import Notifications from '@/mixins/notifications';

import BaseDialog from '@/components/atoms/BaseDialog';
import ConfirmDialog from '@/components/atoms/ConfirmDialog';
import EventThumbnail from '@/components/organisms/EventThumbnail';
import ThumbnailSlider from '@/components/ThumbnailSlider';

import RatView from './RatView';
import InsectV2View from './InsectV2View';
import HykecamView from './HykecamView';
import FlyCountView from './FlyCountView';
import FlyScanView from './FlyScanView';
import FlyView from './FlyView';

export default {
  name: 'GalleryView',
  mixins: [
    EventContainer,
    Notifications,
    SensorTypes,
  ],
  props: {
    requestParams: {  // For EventContainer mixin
      type: Object,
      required: true,
    },
    displayData: {
      type: Object,
      default() {
        return {
          events: [],
          index: null,
          sensor: null,
        };
      },
    },
    visible: Boolean,
  },
  components: {
    BaseDialog,
    ConfirmDialog,
    EventThumbnail,
    FlyCountView,
    FlyScanView,
    FlyView,
    HykecamView,
    InsectV2View,
    RatView,
    ThumbnailSlider,
  },
  computed: {
    dialogVisible: {
      get() {
        return this.visible;
      },
      set(value) {
        this.$emit('update:visible', value);
      },
    },
    current() {
      return this.events[this.index];
    },
    editable() {
      return this.sensor.permissions.includes('full');
    },
    sensor() {
      return this.displayData.sensor || {};
    },
    sensorType() {
      return this.sensor.type;
    },
    sensorId() {  // For EventContainer mixin
      return this.sensor.id;
    },
    updatedEvent() {
      return this.$store.getters.getUpdatedEvent(this.sensorId);
    },
    viewer() {
      switch (this.sensorType) {
        case this.SENSOR_TYPES.INSECT_V2:
          return InsectV2View;
        case this.SENSOR_TYPES.RAT:
          return RatView;
        case this.SENSOR_TYPES.HYKECAM:
          return HykecamView;
        case this.SENSOR_TYPES.FLY_COUNT:
          return FlyCountView;
        case this.SENSOR_TYPES.FLY:
          return FlyView;
        case this.SENSOR_TYPES.FLY_SCAN:
          return FlyScanView;
        default:
          return FlyCountView;
      }
    },
  },
  created() {
    this.updateQueue = createPromiseQueue();
  },
  data() {
    return {
      events: this.displayData.events,
      index: this.displayData.index,
      allEventsLoaded: false,
      deleteEventConfirmDialogVisible: false,
      pendingRemoveTags: [],
    };
  },
  methods: {
    close() {
      this.$emit('visibilityChanged', false);
    },
    deleteEvent: async function () {
      try {
        await this.$store.dispatch('deleteEvent', { event: this.current });
      } catch (error) {
        // ルート遷移によるキャンセルは無視
        if (this.$http.isCancel(error)) {
          return;
        }
        this.notifyError('msg.delete_event_error');
        return;
      }

      this.events.splice(this.index, 1);

      if (this.events.length > 0) {
        // Update following event data because count_difference changed
        // Do this first before adjusting the index
        this.moveEvent(-1);

        // Prevent index to be out of array
        if (this.index === this.events.length) {
          this.index -= 1;
        }

        // Update GalleryView display
        this.indexChanged(this.index);
      } else {
        this.close();
      }

      // Update ActivityList events
      this.$store.dispatch('reloadSensor', this.sensorId);

      this.notifySuccess('msg.deleted');
    },
    indexChanged(value) {
      this.index = value;
      this.$emit('displayedEventChanged', this.events[value]);
    },
    pollEvent: async function (patch) {
      // 子コンポーネントからの pollEvent。patch あり → 楽観的更新、patch なし → 現在 event の再取得
      this.$store.dispatch('pollUpdatingEvent', { event: this.current, patch });
    },
    moveEvent: async function (relativeIndex) {
      // 相対 index で別 event に対して polling を張り替える（delete 後の後続 event 更新など）
      const index = this.index + relativeIndex;
      if (index < 0 || index >= this.events.length) {
        return;
      }
      this.$store.dispatch('pollUpdatingEvent', { event: this.events[index] });
    },
    tryLoadNextEvents: async function () {
      if (!this.allEventsLoaded) {
        try {
          await this.loadNextEvents(16);
        } catch (error) {
          this.notifyError('msg.not_found');
          this.close();
        }
      }
    },
    updateEvent: function (attributes) {
      const pending = this.trackPendingRemoveTags(attributes);
      const filteredAttributes = this.excludePendingRemoveTags(attributes);
      this.updateQueue.enqueue(async () => {
        try {
          await this.$store.dispatch('updateSensorEvent', {
            event: this.current, attributes: filteredAttributes,
          });
        } catch (error) {
          if (this.$http.isCancel(error)) {
            return;
          }
          this.notifyError('msg.update_event_error');
        } finally {
          pending.resolve();
        }
      });
    },
    trackPendingRemoveTags: function (attributes) {
      const removedTags = this.extractRemovedTags(attributes);
      if (removedTags.length > 0) {
        this.pendingRemoveTags = [...this.pendingRemoveTags, ...removedTags];
      }
      return {
        resolve: () => {
          if (removedTags.length > 0) {
            this.pendingRemoveTags = this.pendingRemoveTags.filter(
              pt => !removedTags.some(rt => this._.isEqual(pt, rt)),
            );
          }
        },
      };
    },
    excludePendingRemoveTags: function (attributes) {
      if (attributes.tags == null || this.pendingRemoveTags.length === 0) {
        return attributes;
      }
      return {
        ...attributes,
        tags: attributes.tags.filter(
          tag => !this.pendingRemoveTags.some(pt => this._.isEqual(pt, tag)),
        ),
      };
    },
    extractRemovedTags: function (attributes) {
      if (attributes.tags == null || this.current == null) {
        return [];
      }
      return this.current.tags.filter(t => !attributes.tags.some(at => this._.isEqual(at, t)));
    },
    updateThreshold: async function (attributes) {
      try {
        await this.$store.dispatch('requestUpdateSensor', {
          id: this.sensor.id,
          data: attributes,
        });
        this.notifySuccess('msg.update_threshold_success');
      } catch (error) {
        this.notifyError('msg.update_threshold_error');
      }
    },
  },
  model: {
    prop: 'visible',
    event: 'visibilityChanged',
  },
  provide() {
    // リアクティブなオブジェクトを作成
    const reactiveData = Vue.observable({
      trap_replaced_on: this.sensor.trap_replaced_on,
      trap_effective_days: this.sensor.trap_effective_days,
    });

    // Vue 2 の inject は値のコピーを取るため、プリミティブ値は変更が伝播しない。
    // オブジェクトを provide し、子コンポーネントで obj.prop としてアクセスすることで
    // Vue.observable のリアクティビティが機能する。
    const tagState = Vue.observable({
      pendingRemoveTags: [],
    });

    // センサー情報の変更を監視
    this.$watch('sensor', (newSensor) => {
      reactiveData.trap_replaced_on = newSensor.trap_replaced_on;
      reactiveData.trap_effective_days = newSensor.trap_effective_days;
    }, {
      immediate: true,
      deep: true,
    });

    this.$watch('pendingRemoveTags', (newVal) => {
      tagState.pendingRemoveTags = newVal;
    });

    reactiveData.tagState = tagState;
    return reactiveData;
  },
  watch: {
    displayData(value) {
      this.events = value.events;
      this.allEventsLoaded = false;
      this.eventContainerInternal.lastRetrieved = this.events[this.events.length - 1].timestamp;
      this.indexChanged(value.index);
    },
    updatedEvent(updated) {
      if (updated) {
        const index = this.events.findIndex(ev => ev.id === updated.id);
        if (index !== -1) {
          this.events.splice(index, 1, updated);
          this.$emit('eventUpdated', updated);
        }
      }
    },
  },
};
</script>

<style scoped lang="sass">
.v-card
  background-color: #f2f2f2 !important
  min-height: 100%

.selectable-thumbnail
  min-height: 200px
  max-height: 300px
  width: 300px
  opacity: 0.8
  border: 3px solid
  border-color: transparent

  &.active
    border-color: #0068B6
    opacity: 1.0

::v-deep .v-slide-group
  padding: 0 4px 8px

  &__prev
    margin-right: 4px

  &__next
    margin-left: 4px

  &__prev,
  &__next
    border-radius: 4px
    transition: background-color 0.3s
    background-color: #999

    &:hover
      background-color: #888

    &--disabled
      background-color: #ccc

  .v-icon
    color: white!important
</style>
